import { Injectable } from '@angular/core';
import { HttpService } from '../../utility/http-service';
import { PolicyExplorerResponse, WellLogModel } from './policy-explorer.models';
import { Data } from '@syncfusion/ej2-angular-grids';

export type PolicyExplorerExportFormat = 'csv' | 'json' | 'xml';

@Injectable({ providedIn: 'root' })
export class PolicyExplorerService {
  constructor(private readonly http: HttpService) { }

  open(
    clientId: string,
    schemaName: string,
    tableName: string,
    recordId: string,
    onSuccess: (vm: PolicyExplorerResponse) => void,
    dataSetId?: string | null,
    selectedNodeKey?: string | null,
    page = 1,
    pageSize = 25,
    sortField?: string | null,
    sortDirection?: string | null,
    search?: string | null,
    editMode = false
  ): void {
    let url =
      `api/policy-explorer/${encodeURIComponent(clientId)}` +
      `/${encodeURIComponent(schemaName)}` +
      `/${encodeURIComponent(tableName)}` +
      `/${encodeURIComponent(recordId)}`;

    const params: string[] = [];
    if (dataSetId) params.push(`dataSetId=${encodeURIComponent(dataSetId)}`);
    if (selectedNodeKey) params.push(`selectedNodeKey=${encodeURIComponent(selectedNodeKey)}`);
    params.push(`page=${page}`);
    params.push(`pageSize=${pageSize}`);
    if (sortField) params.push(`sortField=${encodeURIComponent(sortField)}`);
    if (sortDirection) params.push(`sortDirection=${encodeURIComponent(sortDirection)}`);
    if (search) params.push(`search=${encodeURIComponent(search)}`);
    params.push(`editMode=${editMode}`);

    url += `?${params.join('&')}`;
    this.http.get<PolicyExplorerResponse>(url, onSuccess);
  }

  save(
    clientId: string,
    schemaName: string,
    tableName: string,
    recordId: string,
    body: any,
    onSuccess: (result: any) => void
  ): void {
    const url =
      `api/policy-explorer/${encodeURIComponent(clientId)}` +
      `/${encodeURIComponent(schemaName)}` +
      `/${encodeURIComponent(tableName)}` +
      `/${encodeURIComponent(recordId)}/save`;

    this.http.post(url, onSuccess, null, body);
  }

  calculateRecord(
    clientId: string,
    schemaName: string,
    tableName: string,
    recordId: string,
    body: any,
    onSuccess: (result: any) => void
  ): void {
    const url =
      `api/policy-explorer/${encodeURIComponent(clientId)}` +
      `/${encodeURIComponent(schemaName)}` +
      `/${encodeURIComponent(tableName)}` +
      `/${encodeURIComponent(recordId)}/calculate`;

    this.http.post(url, onSuccess, null, body);
  }

  calculateGrid(
    clientId: string,
    schemaName: string,
    tableName: string,
    recordId: string,
    body: any,
    onSuccess: (result: any) => void
  ): void {
    const url =
      `api/policy-explorer/${encodeURIComponent(clientId)}` +
      `/${encodeURIComponent(schemaName)}` +
      `/${encodeURIComponent(tableName)}` +
      `/${encodeURIComponent(recordId)}/calculate-grid`;

    this.http.post(url, onSuccess, null, body);
  }

  getExportUrl(
    format: PolicyExplorerExportFormat,
    clientId: string,
    schemaName: string,
    tableName: string,
    recordId: string,
    dataSetId?: string | null
  ): string {
    let url =
      `api/policy-explorer/${encodeURIComponent(clientId)}` +
      `/${encodeURIComponent(schemaName)}` +
      `/${encodeURIComponent(tableName)}` +
      `/${encodeURIComponent(recordId)}/${format}`;

    const params: string[] = [];
    if (dataSetId) {
      params.push(`dataSetId=${encodeURIComponent(dataSetId)}`);
    }

    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }

    return url;
  }

  export(
    format: PolicyExplorerExportFormat,
    clientId: string,
    schemaName: string,
    tableName: string,
    recordId: string,
    dataSetId?: string | null
  ): void {
    const url = this.getExportUrl(format, clientId, schemaName, tableName, recordId, dataSetId);
    this.http.getFile(url);
  }


  downloadWellLog(
    wellLogId: number
  ): void {
    const url = `api/policy-explorer/${encodeURIComponent(wellLogId)}/download`;
    this.http.getFile(url);
  }

  getWellLogs(
    clientId: string,
    schemaName: string,
    tableName: string,
    recordId: string,
    onSuccess: (vm: WellLogModel) => void,
    dataSetId?: string | null
  ): void {
    let url =
      `api/policy-explorer/${encodeURIComponent(clientId)}` +
      `/${encodeURIComponent(schemaName)}` +
      `/${encodeURIComponent(tableName)}` +
      `/${encodeURIComponent(recordId)}/wellLogs`;

    if (dataSetId) url += `?dataSetId=${encodeURIComponent(dataSetId)}`;
    this.http.get<WellLogModel>(url, onSuccess);
  }
}
